'use client'

import { BodyText, LabelText } from '@/components/ui/typography'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { useTranslations } from 'next-intl'
import { useMemo, useRef, useState } from 'react'
import { IconSearch } from '@tabler/icons-react'
import { useTagTranslations } from '@/hooks/use-tag-translations'
import type { StoryMetadata } from '@/content/stories'
import { filterStoryLibrary, paginateStoryLibrary } from '@/lib/story-library'
import { Reveal } from '@/components/ui/reveal'
import { motion } from 'motion/react'
import { StoryCard } from '@/components/story-card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useLearningStates } from '@/hooks/useLearningState'
import {
  learningContentKey,
  storyLearningLocator
} from '@/lib/learning-state-core'

interface StoryListClientProps {
  stories: StoryMetadata[]
  allLevels: string[]
  storyTopics: string[]
  getStoryHref?: (story: StoryMetadata) => string
  animate?: boolean
}

export function StoryListClient({
  stories: initialStories,
  allLevels,
  storyTopics,
  getStoryHref,
  animate = false
}: StoryListClientProps) {
  const t = useTranslations('read')
  const tCommon = useTranslations('common')
  const { translateLevel, translateStoryTopic } = useTagTranslations()
  const [level, setLevel] = useState<string | undefined>()
  const [topic, setTopic] = useState<string | undefined>()
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const listRef = useRef<HTMLElement>(null)
  const filteredStories = useMemo(
    () => filterStoryLibrary(initialStories, { level, topic, query }),
    [initialStories, level, topic, query]
  )
  const paginated = useMemo(
    () => paginateStoryLibrary(filteredStories, page),
    [filteredStories, page]
  )
  const hasFilters = Boolean(level || topic || query.trim())
  const storyLocators = useMemo(
    () => paginated.stories.map((story) => storyLearningLocator(story.slug)),
    [paginated.stories]
  )
  const { data: learningStates = new Map() } = useLearningStates(storyLocators)
  const goToPage = (nextPage: number) => {
    setPage(nextPage)
    listRef.current?.scrollIntoView({ block: 'start' })
  }

  return (
    <section ref={listRef} className="min-w-0 scroll-mt-24 space-y-5">
      <div className="border-border/70 flex flex-col gap-4 border-y py-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(15rem,1.25fr)_minmax(11rem,1fr)_minmax(11rem,1fr)]">
          <label className="min-w-0 space-y-1.5">
            <LabelText className="text-muted-foreground text-xs tracking-widest uppercase">
              {t('searchStories')}
            </LabelText>
            <span className="relative block">
              <IconSearch className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <Input
                type="search"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                  setPage(1)
                }}
                placeholder={t('searchStoriesPlaceholder')}
                className="h-10 pl-9"
              />
            </span>
          </label>
          <label className="min-w-0 space-y-1.5">
            <LabelText className="text-muted-foreground text-xs tracking-widest uppercase">
              {t('level')}
            </LabelText>
            <Select
              value={level ?? 'all'}
              onValueChange={(value) => {
                setLevel(value === 'all' ? undefined : value)
                setPage(1)
              }}
            >
              <SelectTrigger className="bg-background/80 h-10 w-full min-w-[11rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="start">
                <SelectItem value="all">{tCommon('all')}</SelectItem>
                {allLevels.map((levelOption) => (
                  <SelectItem key={levelOption} value={levelOption}>
                    {translateLevel(levelOption)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="min-w-0 space-y-1.5">
            <LabelText className="text-muted-foreground text-xs tracking-widest uppercase">
              {t('topic')}
            </LabelText>
            <Select
              value={topic ?? 'all'}
              onValueChange={(value) => {
                setTopic(value === 'all' ? undefined : value)
                setPage(1)
              }}
            >
              <SelectTrigger className="bg-background/80 h-10 w-full min-w-[11rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="start">
                <SelectItem value="all">{tCommon('all')}</SelectItem>
                {storyTopics.map((topicOption) => (
                  <SelectItem key={topicOption} value={topicOption}>
                    {translateStoryTopic(topicOption)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </div>

        <div className="flex items-center gap-3 lg:pb-2">
          <BodyText className="text-muted-foreground text-sm">
            {t('showingStories', {
              start: paginated.start,
              end: paginated.end,
              total: filteredStories.length
            })}
          </BodyText>
          {hasFilters && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => {
                setLevel(undefined)
                setTopic(undefined)
                setQuery('')
                setPage(1)
              }}
            >
              {t('clearFilters')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {paginated.stories.map((story, index) => {
          const href = getStoryHref
            ? getStoryHref(story)
            : `/read/${story.slug}`
          const card = (
            <StoryCard
              key={story.slug}
              story={story}
              href={href}
              readStoryText={t('readStory')}
              learningState={learningStates.get(
                learningContentKey(storyLearningLocator(story.slug))
              )}
              className="h-full"
            />
          )

          if (!animate) return card

          return (
            <Reveal
              key={story.slug}
              as={motion.div}
              className="h-full"
              viewportAmount={0.4}
              delay={0.05 * index}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              {card}
            </Reveal>
          )
        })}

        {filteredStories.length === 0 && (
          <div className="text-muted-foreground rounded-lg border border-dashed p-8 text-center text-sm">
            <BodyText className="text-muted-foreground text-sm">
              {hasFilters ? t('noStoriesFound') : t('draftingNewStories')}
            </BodyText>
          </div>
        )}
      </div>

      {paginated.totalPages > 1 && (
        <nav
          aria-label={t('storyPagination')}
          className="flex flex-wrap items-center justify-center gap-2 pt-2"
        >
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={paginated.currentPage === 1}
            onClick={() => goToPage(paginated.currentPage - 1)}
          >
            {t('previous')}
          </Button>
          <span className="text-muted-foreground min-w-28 text-center text-sm">
            {t('pageOf', {
              page: paginated.currentPage,
              total: paginated.totalPages
            })}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={paginated.currentPage === paginated.totalPages}
            onClick={() => goToPage(paginated.currentPage + 1)}
          >
            {t('next')}
          </Button>
        </nav>
      )}
    </section>
  )
}
